import React, { useState } from 'react';
import './UnifiedApp.css';
import './App.css';
import ReconstitutionCalculator from './components/ReconstitutionCalculator';
import DosageCalculator from './components/DosageCalculator';
import SyringeDisplay from './components/SyringeDisplay';
import PeptidesWiki from './components/PeptidesWiki';
import SEOHead from './components/SEOHead';
import { SYRINGE_OPTIONS, DEFAULT_SYRINGE_TYPE } from './constants/syringeTypes';

// Navigation data structure for the sidebar
const navigationData = {
  calculator: {
    title: 'Peptide Calculator',
    component: 'calculator',
  },
  glp1: {
    title: 'GLP-1 Agonists',
    subcategories: {
      established: {
        title: 'FDA Approved',
        items: {
          semaglutide: 'Semaglutide (Ozempic/Wegovy)',
          tirzepatide: 'Tirzepatide (Mounjaro/Zepbound)',
          liraglutide: 'Liraglutide (Victoza/Saxenda)',
          dulaglutide: 'Dulaglutide (Trulicity)',
          exenatide: 'Exenatide (Byetta/Bydureon)',
        },
      },
      nextgen: {
        title: 'In Development',
        items: {
          retatrutide: 'Retatrutide (Triple Agonist)',
          cagrisema: 'CagriSema (GLP-1/Amylin)',
          maritide: 'MariTide (Monthly)',
          pemvidutide: 'Pemvidutide (Dual)',
        },
      },
    },
  },
  wellness: {
    title: 'Wellness Peptides',
    subcategories: {
      healing: {
        title: 'Healing & Recovery',
        items: {
          bpc157: 'BPC-157',
          tesamorelin: 'Tesamorelin',
        },
      },
      growth: {
        title: 'Growth Hormone',
        items: {
          cjc1295: 'CJC-1295',
          ipamorelin: 'Ipamorelin',
          sermorelin: 'Sermorelin',
        },
      },
      muscle: {
        title: 'Muscle Growth',
        items: {
          igf1lr3: 'IGF-1 LR3',
        },
      },
    },
  },
  cosmetic: {
    title: 'Cosmetic Peptides',
    subcategories: {
      skin: {
        title: 'Skin Rejuvenation',
        items: {
          ghkcu: 'GHK-Cu (Copper Peptide)',
          argireline: 'Argireline',
        },
      },
    },
  },
};

// Map navigation keys to wiki component format
const WIKI_CATEGORY_MAP = {
  glp1: 'glp1',
  wellness: 'wellness',
  cosmetic: 'cosmetic',
};

const WIKI_SUBCATEGORY_MAP = {
  established: 'established',
  nextgen: 'nextgen',
  healing: 'healing',
  growth: 'gh',
  muscle: 'muscle',
  skin: 'skin',
};

function UnifiedApp() {
  const [activeView, setActiveView] = useState('calculator');
  const [selectedCategory, setSelectedCategory] = useState('glp1');
  const [selectedSubcategory, setSelectedSubcategory] = useState('established');
  const [selectedItem, setSelectedItem] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Calculator states
  const [activeTab, setActiveTab] = useState('reconstitution');
  const [syringeType, setSyringeType] = useState(DEFAULT_SYRINGE_TYPE);
  const [fillLevel, setFillLevel] = useState(0);
  const [currentDose, setCurrentDose] = useState(null);

  const handleNavigation = (view, category = null, subcategory = null, item = null) => {
    setActiveView(view);
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    setSelectedItem(item);
    setSidebarOpen(false);
  };

  const renderContent = () => {
    if (activeView === 'calculator') {
      return (
        <div className="App">
          <SEOHead
            title="Peptide Calculator - Free Reconstitution & Dosage Tool | PeptideDose"
            description="Calculate peptide reconstitution and dosage for Retatrutide, Semaglutide, Tirzepatide, and more. Professional calculator with syringe visualization."
            isCalculator={true}
          />
          <header className="app-header">
            <div className="header-title-section">
              <h1 className="app-title">
                <span className="title-accent">Peptide</span> Calculator
              </h1>
              <p className="app-subtitle">Precision dosing for injectable peptides</p>
            </div>
          </header>

          <div className="main-container">
            <div className="calculator-section">
              <div className="tab-container">
                <button
                  className={`tab-button ${activeTab === 'reconstitution' ? 'active' : ''}`}
                  onClick={() => setActiveTab('reconstitution')}
                >
                  <span className="tab-icon">💧</span>
                  Reconstitution
                </button>
                <button
                  className={`tab-button ${activeTab === 'dosage' ? 'active' : ''}`}
                  onClick={() => setActiveTab('dosage')}
                >
                  <span className="tab-icon">💉</span>
                  Dosage
                </button>
              </div>

              <div className="calculator-content">
                {activeTab === 'reconstitution' ? (
                  <ReconstitutionCalculator />
                ) : (
                  <DosageCalculator
                    setFillLevel={setFillLevel}
                    syringeType={syringeType}
                    setCurrentDose={setCurrentDose}
                  />
                )}
              </div>
            </div>

            <div className="syringe-section">
              <div className="syringe-controls">
                <label className="syringe-select-label">Select Syringe Type:</label>
                <select
                  className="syringe-select"
                  value={syringeType}
                  onChange={(e) => setSyringeType(e.target.value)}
                >
                  {SYRINGE_OPTIONS.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <SyringeDisplay
                fillLevel={fillLevel}
                syringeType={syringeType}
                currentDose={currentDose}
              />
            </div>
          </div>

          <footer className="app-footer">
            <p>Always consult with a healthcare professional before use</p>
          </footer>
        </div>
      );
    } else {
      return (
        <div className="wiki-integrated-view">
          <PeptidesWiki
            initialCategory={WIKI_CATEGORY_MAP[selectedCategory] || 'glp1'}
            initialSubcategory={WIKI_SUBCATEGORY_MAP[selectedSubcategory] || 'established'}
            initialPeptide={selectedItem || 'semaglutide'}
            hideSidebar={true}
          />
        </div>
      );
    }
  };

  return (
    <div className="unified-app">
      <button
        className={`hamburger-menu ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <nav className="sidebar-nav">
          {/* Main Calculator Button */}
          <button
            className={`nav-item nav-main ${activeView === 'calculator' ? 'active' : ''}`}
            onClick={() => handleNavigation('calculator')}
          >
            {navigationData.calculator.title}
          </button>

          <div className="nav-divider"></div>

          {/* Wiki Categories */}
          {Object.entries(navigationData).map(([key, category]) => {
            if (key === 'calculator') return null;

            return (
              <div key={key} className="nav-category">
                <button
                  className={`nav-category-title ${selectedCategory === key ? 'active' : ''}`}
                  onClick={() => {
                    if (selectedCategory === key) {
                      setSelectedCategory(null);
                      setSelectedSubcategory(null);
                      setSelectedItem(null);
                    } else {
                      setSelectedCategory(key);
                      setSelectedSubcategory(null);
                      setSelectedItem(null);
                    }
                  }}
                >
                  <span className="nav-arrow">{selectedCategory === key ? '−' : '+'}</span>
                  {category.title}
                </button>

                {selectedCategory === key && category.subcategories && (
                  <div className="nav-subcategories">
                    {Object.entries(category.subcategories).map(([subKey, subcategory]) => (
                      <div key={subKey} className="nav-subcategory">
                        <button
                          className={`nav-subcategory-title ${selectedSubcategory === subKey ? 'active' : ''}`}
                          onClick={() => {
                            if (selectedSubcategory === subKey) {
                              setSelectedSubcategory(null);
                              setSelectedItem(null);
                            } else {
                              setSelectedSubcategory(subKey);
                              setSelectedItem(null);
                            }
                          }}
                        >
                          <span className="nav-arrow">
                            {selectedSubcategory === subKey ? '−' : '+'}
                          </span>
                          {subcategory.title}
                        </button>

                        {selectedSubcategory === subKey && (
                          <ul className="nav-items">
                            {Object.entries(subcategory.items).map(([itemKey, itemName]) => (
                              <li key={itemKey}>
                                <button
                                  className={`nav-item ${selectedItem === itemKey ? 'active' : ''}`}
                                  onClick={() => handleNavigation('wiki', key, subKey, itemKey)}
                                >
                                  {itemName}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="quick-links">
            <a href="/retatrutide-calculator" className="footer-link">
              Retatrutide Calculator
            </a>
            <a href="/faq" className="footer-link">
              FAQ
            </a>
            <a href="/guides" className="footer-link">
              Guides
            </a>
            <a href="/peptides/compare" className="footer-link">
              Compare Peptides
            </a>
            <a href="/tools/dosage-converter" className="footer-link">
              Dosage Converter
            </a>
          </div>
          <p className="disclaimer-text">
            Educational purposes only. Consult healthcare professionals.
          </p>
        </div>
      </aside>

      <main className="app-main">{renderContent()}</main>

      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
}

export default UnifiedApp;
